import Dexie, { type Table } from 'dexie';

export interface CacheEntry<T = unknown> {
  key: string;
  familyId: string | null;
  data: T;
  updatedAt: number;
}

export interface OutboxMutation {
  id?: number;
  familyId: string | null;
  path: string;
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body: string | null;
  clientId: string;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

export interface MetaEntry {
  key: string;
  value: string;
}

export interface ConflictEntry {
  id?: number;
  familyId: string | null;
  entityType: string;
  local: string;
  server: string;
  createdAt: number;
}

class HeritageDatabase extends Dexie {
  cache!: Table<CacheEntry, string>;
  outbox!: Table<OutboxMutation, number>;
  meta!: Table<MetaEntry, string>;
  conflicts!: Table<ConflictEntry, number>;

  constructor() {
    super('heritage');
    this.version(1).stores({
      cache: '&key, familyId, updatedAt',
      outbox: '++id, createdAt, familyId, clientId',
      meta: '&key',
      conflicts: '++id, createdAt, familyId',
    });
  }
}

let instance: HeritageDatabase | null = null;

export function db(): HeritageDatabase | null {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return null;
  instance ??= new HeritageDatabase();
  return instance;
}

export async function cacheSet<T>(
  key: string,
  data: T,
  familyId: string | null = null,
): Promise<void> {
  const database = db();
  if (!database) return;
  await database.cache.put({ key, data, familyId, updatedAt: Date.now() });
}

export async function cacheGet<T>(key: string): Promise<T | null> {
  const database = db();
  if (!database) return null;
  const entry = await database.cache.get(key);
  return (entry?.data as T | undefined) ?? null;
}

export async function cacheDelete(key: string): Promise<void> {
  const database = db();
  if (!database) return;
  await database.cache.delete(key);
}

export function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function enqueueMutation(
  mutation: Omit<OutboxMutation, 'id' | 'createdAt' | 'attempts' | 'lastError' | 'clientId'> & {
    clientId?: string;
  },
): Promise<void> {
  const database = db();
  if (!database) return;
  await database.outbox.add({
    ...mutation,
    clientId: mutation.clientId ?? newClientId(),
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  });
}

export async function pendingMutations(): Promise<number> {
  const database = db();
  if (!database) return 0;
  return database.outbox.count();
}

export async function pendingConflicts(): Promise<number> {
  const database = db();
  if (!database) return 0;
  return database.conflicts.count();
}

export async function clearLocalData(): Promise<void> {
  const database = db();
  if (!database) return;
  await Promise.all([
    database.cache.clear(),
    database.outbox.clear(),
    database.meta.clear(),
    database.conflicts.clear(),
  ]);
}

export async function requestPersistentStorage(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return;
  try {
    await navigator.storage.persist();
  } catch {
    // Non-critical: the browser may refuse persistent storage.
  }
}
